import React, { useMemo, useRef, useState } from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { Link, useNavigate } from 'react-router-dom';
import '../styles/Home.css';
import { useLanguage } from '../context/LanguageContext';
import { translations } from '../translations/translations';
import { ProjectList } from '../helpers/ProjectList';
import { PILLARS, pillarOf, skillsWithProjects } from '../helpers/SkillsData';
import {
  FaRobot,
  FaCode,
  FaBolt,
  FaCogs,
  FaArrowRight,
  FaPause,
  FaPlay,
  FaRocket,
  FaServer,
  FaGlobe,
} from 'react-icons/fa';

// Real design artifacts from project 32 (AirFinity telemetry gateway), in
// build order. bg matches each export's own canvas so `object-fit: contain`
// letterboxing is invisible.
const DESIGN_STORY_STEPS = [
  { key: 'schematic', img: '/assets/Project32_11.jpg', bg: '#f5f4ef' },
  { key: 'layout', img: '/assets/Project32_10.jpg', bg: '#0b1220' },
  { key: 'board', img: '/assets/Project32_4.jpg', bg: '#f4f4f8' },
  { key: 'product', img: '/assets/Project32_9.jpg', bg: '#f4f4f8' },
];

// The four mechatronics disciplines, each fronted by a real flagship visual:
// the AirFinity enclosure x-rayed to its PCB (mechanical), the AirFinity
// dashboard site (electrical), the Ez-RPH product hero (software) and the
// OmniChat landing page (AI). Cover alt text lives in
// translations[lang].home.pillarCoverAlts so it localizes with the page.
// Counts and top skills are derived, never hand-written.
const PILLAR_META = {
  mechanical: { icon: <FaCogs />, img: '/assets/pillar-mech-xray.jpg' },
  electrical: { icon: <FaBolt />, img: '/assets/Project32_1.jpg' },
  programming: { icon: <FaCode />, img: '/assets/pillar-ezrph-hero.jpg' },
  ai: { icon: <FaRobot />, img: '/assets/Project31_1.jpg' },
};

// The ops side of the story — build, ship, run, connect. Copy lives in
// translations[lang].home.deploySteps; proof is project 40 (the homelab).
const DEPLOY_STEPS = [
  { key: 'build', icon: <FaCode /> },
  { key: 'ship', icon: <FaRocket /> },
  { key: 'run', icon: <FaServer /> },
  { key: 'reach', icon: <FaGlobe /> },
];

function Home() {
  const { language } = useLanguage();
  const t = translations[language].home;
  const ts = translations[language].skillsPage;
  const navigate = useNavigate();
  // Respect prefers-reduced-motion: show the hero reel's poster as a still
  const reduceMotion = useReducedMotion();
  const heroVideoRef = useRef(null);
  const [heroPaused, setHeroPaused] = useState(false);

  const toggleHeroVideo = (e) => {
    // The button sits inside the card link — don't navigate
    e.preventDefault();
    e.stopPropagation();
    const v = heroVideoRef.current;
    if (!v) return;
    if (v.paused) {
      v.play();
      setHeroPaused(false);
    } else {
      v.pause();
      setHeroPaused(true);
    }
  };

  const caseStudies = ProjectList.filter((p) => p.section === 'case-study').sort(
    (a, b) => (a.rank || 99) - (b.rank || 99)
  );

  // Honest, derived stats — every number is computed from the project registry
  const liveProducts = ProjectList.filter((p) => p.inProduction).length;
  const firstYear = Math.min(...ProjectList.map((p) => p.year));
  const yearsBuilding = new Date().getFullYear() - firstYear;
  const skills = useMemo(() => skillsWithProjects(), []);

  // The four disciplines with real counts; top skills = the most project-backed ones
  const pillars = useMemo(
    () =>
      PILLARS.map((key) => {
        const inPillar = skills.filter((s) => pillarOf(s.category) === key);
        const projectIds = new Set(
          inPillar.flatMap((s) => s.projects.map((p) => p.projectId))
        );
        return {
          key,
          skillCount: inPillar.length,
          projectCount: projectIds.size,
          top: [...inPillar]
            .sort((a, b) => b.projects.length - a.projects.length)
            .slice(0, 4)
            .map((s) => s.name),
        };
      }).filter((c) => c.skillCount > 0),
    [skills]
  );

  return (
    <div className='home'>
      {/* Animated background elements */}
      <div className='bg-decoration'>
        <div className='gradient-orb orb-1'></div>
        <div className='gradient-orb orb-2'></div>
        <div className='gradient-orb orb-3'></div>
      </div>

      <div className='hero-section hero-split'>
        <motion.div
          className='hero-content'
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          <motion.span
            className='status-badge'
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.3, duration: 0.5 }}
          >
            <span className='status-dot'></span>
            {t.openTo}
          </motion.span>
          {/* Not a heading — the outline is h1 name, then h2 headline */}
          <p className='greeting'>{t.greeting}</p>
          <h1 className='name gradient-text'>{t.name}</h1>
          <h2 className='headline'>{t.headline}</h2>
          {/* The four disciplines, each deep-linking into its pillar */}
          <div className='hero-disciplines'>
            {PILLARS.map((key) => (
              <Link key={key} to={`/skills?pillar=${key}`} className='hero-discipline-chip'>
                {PILLAR_META[key].icon} {ts.pillars[key].name}
              </Link>
            ))}
          </div>
          <p className='bio'>{t.bio}</p>
          <motion.div
            className='hero-cta'
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5, duration: 0.5 }}
          >
            <a href='#featured' className='cta-button primary'>
              {t.ctaPrimary}
            </a>
            <Link to='/projects' className='cta-button secondary'>
              {t.ctaSecondary}
            </Link>
          </motion.div>
        </motion.div>

        {/* Real hardware reel: KiCad PCB turntable, then the enclosure
            exploded-view — electronics design and mechanical design in one */}
        <motion.div
          className='hero-model glass'
          initial={{ opacity: 0, x: 40 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.4, duration: 0.7 }}
        >
          <Link to='/skills' className='hero-model-link'>
            <div className='hero-model-media'>
              <video
                ref={heroVideoRef}
                src='/assets/airfinity-hero-2.mp4'
                poster='/assets/airfinity-hero-poster.jpg'
                autoPlay={!reduceMotion}
                muted
                loop
                playsInline
                aria-label={t.heroModelCaption}
              />
              <button
                type='button'
                className='hero-model-toggle'
                onClick={toggleHeroVideo}
                aria-label={heroPaused ? t.heroPlay : t.heroPause}
              >
                {heroPaused ? <FaPlay /> : <FaPause />}
              </button>
            </div>
            <p className='hero-model-caption'>{t.heroModelCaption}</p>
            <span className='hero-model-cta'>
              {t.heroModelCta} <FaArrowRight />
            </span>
          </Link>
        </motion.div>
      </div>

      {/* Stats section — all four numbers derive from the live project data */}
      <motion.div
        className='stats-section'
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.6 }}
      >
        <div
          className='stat-item stat-clickable'
          role='button'
          tabIndex={0}
          onClick={() => document.getElementById('featured')?.scrollIntoView({ behavior: 'smooth' })}
          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); document.getElementById('featured')?.scrollIntoView({ behavior: 'smooth' }); } }}
        >
          <span className='stat-number'>{liveProducts}</span>
          <span className='stat-label'>{t.statsProducts}</span>
        </div>
        <div className='stat-divider'></div>
        <div className='stat-item'>
          <span className='stat-number'>{yearsBuilding}+</span>
          <span className='stat-label'>{t.statsYears}</span>
        </div>
        <div className='stat-divider'></div>
        <div
          className='stat-item stat-clickable'
          role='button'
          tabIndex={0}
          onClick={() => navigate('/projects')}
          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); navigate('/projects'); } }}
        >
          <span className='stat-number'>{ProjectList.length}</span>
          <span className='stat-label'>{t.statsProjects}</span>
        </div>
        <div className='stat-divider'></div>
        <div
          className='stat-item stat-clickable'
          role='button'
          tabIndex={0}
          onClick={() => navigate('/skills')}
          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); navigate('/skills'); } }}
        >
          <span className='stat-number'>{skills.length}</span>
          <span className='stat-label'>{t.statsSkills}</span>
        </div>
      </motion.div>

      {/* The four disciplines — the mechatronics claim with derived counts,
          followed immediately by its proof (the design story below) */}
      <div className='pillars-section' id='skills'>
        <motion.div
          className='section-header'
          initial={reduceMotion ? false : { opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <h1 className='section-title'>{t.skillsTitle}</h1>
          <p className='section-subtitle'>{t.skillsSubtitle}</p>
        </motion.div>
        <div className='pillar-grid'>
          {pillars.map((pillar, index) => (
            <motion.div
              key={pillar.key}
              initial={reduceMotion ? false : { opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: Math.min(index * 0.08, 0.4) }}
              whileHover={reduceMotion ? undefined : { scale: 1.03, y: -5 }}
            >
              <Link className='pillar-card glass' to={`/skills?pillar=${pillar.key}`}>
                <div className='pillar-card-media'>
                  <img
                    src={PILLAR_META[pillar.key].img}
                    alt={t.pillarCoverAlts[pillar.key]}
                    loading='lazy'
                  />
                </div>
                <div className='pillar-card-body'>
                  <div className='pillar-card-head'>
                    <span className='pillar-icon' aria-hidden='true'>
                      {PILLAR_META[pillar.key].icon}
                    </span>
                    <h2>{ts.pillars[pillar.key].name}</h2>
                  </div>
                  <p className='pillar-card-tagline'>{ts.pillars[pillar.key].tagline}</p>
                  <p className='category-count'>
                    {pillar.skillCount}{' '}
                    {pillar.skillCount === 1 ? ts.skillWordSingular : ts.skillsWord} ·{' '}
                    {pillar.projectCount}{' '}
                    {pillar.projectCount === 1 ? ts.projectSingular : ts.projectPlural}
                  </p>
                  <div className='category-chips'>
                    {pillar.top.map((name) => (
                      <span key={name} className='tech-chip'>{name}</span>
                    ))}
                  </div>
                  <span className='category-cta'>
                    {t.categoryCta} <FaArrowRight />
                  </span>
                </div>
              </Link>
            </motion.div>
          ))}
        </div>
        <motion.div
          className='skills-cta'
          initial={reduceMotion ? false : { opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
        >
          <Link to='/skills' className='cta-button secondary'>
            {t.skillsCta} <FaArrowRight />
          </Link>
        </motion.div>
      </div>

      {/* Design story — the telemetry gateway from schematic to shipped
          product, every image exported from the actual design files */}
      <div className='design-story'>
        <motion.div
          className='section-header'
          initial={reduceMotion ? false : { opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <h1 className='section-title'>{t.designStoryTitle}</h1>
          <p className='section-subtitle'>{t.designStorySubtitle}</p>
        </motion.div>
        <div className='design-story-grid'>
          {DESIGN_STORY_STEPS.map((step, index) => (
            <motion.div
              key={step.key}
              initial={reduceMotion ? false : { opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: Math.min(index * 0.08, 0.4) }}
              whileHover={reduceMotion ? undefined : { scale: 1.03, y: -5 }}
            >
              <Link to='/project/32' className='design-step glass'>
                <div
                  className='design-step-media'
                  style={{ backgroundColor: step.bg }}
                >
                  {/* The visible label + caption below fully describe the
                      tile, so the image is redundant for screen readers */}
                  <img src={step.img} alt='' loading='lazy' />
                </div>
                <div className='design-step-body'>
                  <span className='design-step-num' aria-hidden='true'>
                    {index + 1}
                  </span>
                  <div>
                    <h2>{t.designSteps[step.key].label}</h2>
                    <p>{t.designSteps[step.key].caption}</p>
                  </div>
                </div>
              </Link>
            </motion.div>
          ))}
        </div>
        <motion.div
          className='design-story-cta'
          initial={reduceMotion ? false : { opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
        >
          <Link to='/project/32' className='cta-button secondary'>
            {t.designStoryCta} <FaArrowRight />
          </Link>
        </motion.div>
      </div>

      {/* From code to production — the ops half of the pitch: the person who
          writes the product also ships it, runs it and owns the network it
          lives on. Proof: project 40, the self-hosted infrastructure. */}
      <div className='deploy-section' id='deploy'>
        <motion.div
          className='section-header'
          initial={reduceMotion ? false : { opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <h1 className='section-title'>{t.deployTitle}</h1>
          <p className='section-subtitle'>{t.deploySubtitle}</p>
        </motion.div>
        <div className='deploy-grid'>
          <div className='deploy-steps'>
            {DEPLOY_STEPS.map((step, index) => (
              <motion.div
                key={step.key}
                className='deploy-step glass'
                initial={reduceMotion ? false : { opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: Math.min(index * 0.08, 0.4) }}
              >
                <span className='deploy-step-icon' aria-hidden='true'>
                  {step.icon}
                </span>
                <div>
                  <h2>{t.deploySteps[step.key].label}</h2>
                  <p>{t.deploySteps[step.key].caption}</p>
                </div>
              </motion.div>
            ))}
          </div>
          <motion.div
            className='deploy-media-wrap'
            initial={reduceMotion ? false : { opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <Link to='/project/40' className='deploy-media glass'>
              <img src='/assets/Project40_1.jpg' alt={t.deployMediaAlt} loading='lazy' />
              <div className='deploy-media-body'>
                <p className='deploy-media-caption'>{t.deployMediaCaption}</p>
                <span className='category-cta'>
                  {t.deployCta} <FaArrowRight />
                </span>
              </div>
            </Link>
          </motion.div>
        </div>
      </div>

      {/* Featured case studies */}
      <div className='featured-section' id='featured'>
        <motion.div
          className='section-header'
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <h1 className='section-title'>{t.featuredTitle}</h1>
          <p className='section-subtitle'>{t.featuredSubtitle}</p>
        </motion.div>
        <div className='featured-grid'>
          {caseStudies.map((project, index) => (
            <motion.div
              key={project.projectId}
              className='featured-card glass'
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              whileHover={{ scale: 1.02, y: -5 }}
              role='link'
              tabIndex={0}
              aria-label={project.name}
              onClick={() => navigate('/project/' + project.projectId)}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); navigate('/project/' + project.projectId); } }}
            >
              <div
                className='featured-card-image'
                style={{ backgroundImage: `url(${project.image})` }}
              >
                {project.status && (
                  <span className='featured-card-status'>{project.status}</span>
                )}
              </div>
              <div className='featured-card-body'>
                <h2>{project.name}</h2>
                <p className='featured-card-tagline'>{project.tagline}</p>
                <div className='featured-card-tech'>
                  {(project.technologies || []).slice(0, 4).map((tech) => (
                    <span key={tech} className='tech-chip'>{tech}</span>
                  ))}
                </div>
                <span className='featured-card-cta'>
                  {t.viewCaseStudy} <FaArrowRight />
                </span>
              </div>
            </motion.div>
          ))}
        </div>
      </div>

    </div>
  );
}

export default Home;
